import type { TiptapContent, TiptapContentNode } from '../tiptap/tiptapTypes';
import { TiptapNodeType } from '../tiptap/tiptapTypes';
import { mapTiptapNodes } from '../tiptap/utils/jsonContentUtils/mapTiptapNodes';
import { addTaskItemLabelIds } from '../tiptap/utils/taskItemLabelIdUtils';

// The equivalent check over live editor content - which the editor's save timing depends on -
// lives beside its only caller, in `tiptap/utils/taskItemLabelIdUtils`.

/**
 * Collects the unique label IDs referenced by task items in a single-editor task list document.
 */
export const getTaskListLabelIds = (document: TiptapContent): string[] => {
    const labelIds = new Set<string>();

    // Recurse over each node
    const visitNode = (node: TiptapContentNode): void => {
        if (node.type === TiptapNodeType.taskItem) addTaskItemLabelIds(node.attrs?.labels, labelIds);
        node.content?.forEach(visitNode);
    };

    visitNode(document);
    return [...labelIds];
};

/**
 * Removes a global label reference from every task item in a single-editor task list document.
 */
export const removeTaskListLabelReferences = (document: TiptapContent, labelId: string): TiptapContent =>
    mapTiptapNodes(document, (node) => {
        if (node.type !== TiptapNodeType.taskItem || !Array.isArray(node.attrs?.labels)) return node;

        const labels = node.attrs.labels;
        const updatedLabels = labels.filter((label) => (label as { labelId?: unknown } | null)?.labelId !== labelId);

        if (updatedLabels.length === labels.length) return node;

        return {
            ...node,
            attrs: {
                ...node.attrs,
                labels: updatedLabels,
            },
        };
    }) as TiptapContent;
